import { useState, useEffect } from "react";
import { ZONE_COLORS } from "./RunDetail/utils";

const ZONE_NAMES = { 1: "Recovery", 2: "Aerobic Base", 3: "Aerobic Threshold", 4: "Lactate Threshold", 5: "VO₂ Max" };

function zoneRanges(cfg) {
  return [
    { z: 1, label: `≤ ${cfg.z1_max} bpm`,          color: ZONE_COLORS[1] },
    { z: 2, label: `${cfg.z1_max + 1}–${cfg.z2_max} bpm`, color: ZONE_COLORS[2] },
    { z: 3, label: `${cfg.z2_max + 1}–${cfg.z3_max} bpm`, color: ZONE_COLORS[3] },
    { z: 4, label: `${cfg.z3_max + 1}–${cfg.z4_max} bpm`, color: ZONE_COLORS[4] },
    { z: 5, label: `≥ ${cfg.z4_max + 1} bpm`,       color: ZONE_COLORS[5] },
  ];
}

function ZoneBar({ cfg }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
      {zoneRanges(cfg).map(({ z, label, color }) => (
        <div key={z} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: color + "33", border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 12, fontWeight: "bold", color }}>{z}</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: "bold", color: "var(--text-primary)" }}>{ZONE_NAMES[z]}</div>
            <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 1 }}>{label}</div>
          </div>
          <div style={{ height: 8, width: 80, borderRadius: 99, background: color, opacity: 0.7, flexShrink: 0 }} />
        </div>
      ))}
    </div>
  );
}

function ConfigCard({ cfg, isActive, onApply }) {
  const date = new Date(cfg.effective_from).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return (
    <div style={{ border: `1.5px solid ${isActive ? "#2E75B6" : "var(--border)"}`, borderRadius: 10, overflow: "hidden", background: "var(--bg-card)" }}>
      <div style={{ padding: "12px 16px", background: isActive ? "#0D2137" : "var(--bg-card)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: "bold", color: "var(--text-primary)" }}>{cfg.name}</div>
          <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 2 }}>Effective from {date}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isActive && (
            <span style={{ fontSize: 10, background: "#2E75B6", color: "#fff", padding: "2px 8px", borderRadius: 10, fontWeight: "bold" }}>ACTIVE</span>
          )}
          {!isActive && (
            <button
              onClick={onApply}
              style={{ fontSize: 12, padding: "5px 12px", borderRadius: 6, border: "1.5px solid #2E75B6", background: "transparent", color: "#2E75B6", cursor: "pointer", fontFamily: "'Georgia', serif" }}
            >
              Apply
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: "0 16px 16px" }}>
        <ZoneBar cfg={cfg} />
      </div>
    </div>
  );
}

const EMPTY_FORM = { name: "", effective_from: new Date().toISOString().slice(0, 10), z1_max: "", z2_max: "", z3_max: "", z4_max: "" };

export default function HrZones({ selectedZoneConfigId, onSelectZoneConfig }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  async function loadConfigs() {
    setLoading(true);
    try {
      const res = await fetch("/api/hr-zones", { headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } });
      setConfigs(await res.json());
    } finally {
      setLoading(false);
    }
  }

  async function saveConfig() {
    setError(null);
    const { name, effective_from, z1_max, z2_max, z3_max, z4_max } = form;
    if (!name.trim() || !effective_from || !z1_max || !z2_max || !z3_max || !z4_max) {
      setError("All fields are required.");
      return;
    }
    const nums = [+z1_max, +z2_max, +z3_max, +z4_max];
    if (nums.some(n => !Number.isInteger(n) || n < 50 || n > 250)) {
      setError("Thresholds must be whole numbers between 50 and 250.");
      return;
    }
    if (!(nums[0] < nums[1] && nums[1] < nums[2] && nums[2] < nums[3])) {
      setError("Each zone threshold must be greater than the previous.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch("/api/hr-zones", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
        body: JSON.stringify({ name: name.trim(), effective_from, z1_max: nums[0], z2_max: nums[1], z3_max: nums[2], z4_max: nums[3] }),
      });
      if (!res.ok) {
        const body = await res.json();
        setError(body.error || "Save failed.");
        return;
      }
      const created = await res.json();
      setConfigs(prev => [created, ...prev]);
      onSelectZoneConfig(created.id);
      setForm(EMPTY_FORM);
      setShowForm(false);
    } catch {
      setError("Save failed.");
    } finally {
      setSaving(false);
    }
  }

  const activeId = selectedZoneConfigId ?? (configs[0]?.id ?? null);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "var(--text-subtle)" }}>Loading...</div>;

  return (
    <div style={{ fontFamily: "'Georgia', serif", background: "var(--bg-page)", minHeight: "100vh", paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ background: "#1A1A2E", padding: "20px 20px 16px" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, letterSpacing: 2, fontFamily: "monospace" }}>HEART RATE ZONES</div>
          <div style={{ color: "#fff", fontSize: 18, fontWeight: "bold", marginTop: 3 }}>Zone Configuration</div>
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, marginTop: 2 }}>
            Zones are applied to all run data and progress tracking
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: "0 auto", padding: "20px" }}>
        {/* Config history */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
          {configs.map(cfg => (
            <ConfigCard
              key={cfg.id}
              cfg={cfg}
              isActive={cfg.id === activeId}
              onApply={() => onSelectZoneConfig(cfg.id)}
            />
          ))}
        </div>

        {/* Add new config */}
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            style={{ width: "100%", padding: "12px", border: "1.5px dashed var(--border)", borderRadius: 10, background: "transparent", color: "var(--text-subtle)", fontFamily: "'Georgia', serif", fontSize: 13, cursor: "pointer" }}
          >
            + Add new zone config
          </button>
        ) : (
          <div style={{ border: "1.5px solid var(--border)", borderRadius: 10, padding: 20, background: "var(--bg-card)" }}>
            <div style={{ fontSize: 13, fontWeight: "bold", color: "var(--text-primary)", marginBottom: 16 }}>New Zone Configuration</div>

            {error && <div style={{ color: "#C0392B", fontSize: 12, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "var(--text-faint)", fontFamily: "monospace", letterSpacing: 1 }}>NAME</span>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Spring 2026"
                    style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-page)", color: "var(--text-primary)", fontFamily: "'Georgia', serif", fontSize: 13 }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "var(--text-faint)", fontFamily: "monospace", letterSpacing: 1 }}>EFFECTIVE FROM</span>
                  <input type="date" value={form.effective_from} onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
                    style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-page)", color: "var(--text-primary)", fontFamily: "'Georgia', serif", fontSize: 13 }} />
                </label>
              </div>

              <div style={{ fontSize: 11, color: "var(--text-faint)", fontFamily: "monospace", letterSpacing: 1, marginTop: 4 }}>ZONE UPPER BOUNDS (bpm)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                {[
                  { key: "z1_max", label: "Zone 1 max", color: ZONE_COLORS[1] },
                  { key: "z2_max", label: "Zone 2 max", color: ZONE_COLORS[2] },
                  { key: "z3_max", label: "Zone 3 max", color: ZONE_COLORS[3] },
                  { key: "z4_max", label: "Zone 4 max", color: ZONE_COLORS[4] },
                ].map(({ key, label, color }) => (
                  <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontSize: 10, color, fontWeight: "bold" }}>{label}</span>
                    <input
                      type="number" min="50" max="250"
                      value={form[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      placeholder="—"
                      style={{ padding: "8px 6px", borderRadius: 6, border: `1px solid ${color}44`, background: "var(--bg-page)", color: "var(--text-primary)", fontFamily: "monospace", fontSize: 13, width: "100%", boxSizing: "border-box" }}
                    />
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-faint)", fontStyle: "italic" }}>
                Zone 5 starts at Zone 4 max + 1 bpm and has no upper bound.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setError(null); }}
                style={{ flex: 1, padding: "10px", border: "1.5px solid var(--border)", borderRadius: 8, background: "transparent", color: "var(--text-subtle)", fontFamily: "'Georgia', serif", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={saveConfig} disabled={saving}
                style={{ flex: 2, padding: "10px", border: "none", borderRadius: 8, background: saving ? "#333" : "#2E75B6", color: "#fff", fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: "bold", cursor: saving ? "default" : "pointer" }}>
                {saving ? "Saving..." : "Save & Apply"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
